import React, { useEffect, useState } from 'react';
import { useParams, Link, Navigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowRight, CheckCircle2, Phone, Calculator, X, ArrowLeft } from 'lucide-react';
import { MaterialCalculator } from '../components/MaterialCalculator';
import { blogPosts } from '../data/blog';
export function BlogPost() {
  const {
    slug
  } = useParams<{
    slug: string;
  }>();
  const [showCalcModal, setShowCalcModal] = useState(false);
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);
  const post = blogPosts.find(p => p.slug === slug);
  if (!post) {
    return <Navigate to="/blog" replace data-id="element-2269" data-mp="36gb0rjqzxn8o" />;
  }
  return <main className="w-full bg-[#fdfbf7] dark:bg-[#2a2a2a] transition-colors duration-300 min-h-screen" data-id="element-2270" data-mp="31gy467abp1sw">
      {/* Hero */}
      <section className="relative py-20 md:py-28 bg-[#333333] overflow-hidden" data-id="element-2271" data-mp="14oo5v73zh86x">
        <div className="absolute inset-0 bg-cover bg-center opacity-30" style={{
        backgroundImage: `url(${post.image})`
      }} data-id="element-2272" data-mp="zos4lfmo7uv2" />
        <div className="absolute inset-0 bg-gradient-to-b from-[#333333]/40 to-[#333333]/80" data-id="element-2273" data-mp="113ip669asypv" />
        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-2274" data-mp="164ya7ybh7589">
          <Link to="/blog" className="inline-flex items-center text-brand-orange hover:text-white transition-colors mb-6 font-bold text-sm uppercase tracking-widest" data-id="element-2275" data-mp="s5fe63vxvkcb">
            <ArrowLeft className="w-4 h-4 mr-1" data-id="element-2276" data-mp="1ktbav8lidc93" />
            Back to Blog
          </Link>
          <motion.h1 initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} className="font-heading text-4xl sm:text-5xl lg:text-6xl font-extrabold text-white tracking-tight mb-6 uppercase" data-id="element-2277" data-mp="1oun03d0gzvgr">
            {post.title}
          </motion.h1>
          <motion.p initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.1
        }} className="text-lg text-stone-300 font-medium font-sub max-w-2xl mx-auto" data-id="element-2278" data-mp="23rx8gdhtgl89">
            {post.date}
          </motion.p>
        </div>
      </section>

      {post.type === 'guide' ? <>
          {/* Quick Info Bar */}
          {post.quickInfo && <section className="bg-[#3a3a3a] border-y border-[#4a4a4a] py-5" data-id="element-2279" data-mp="28fn89m9a6d3s">
              <div className="max-w-4xl mx-auto px-4 flex flex-wrap justify-center gap-x-10 gap-y-3 text-sm text-stone-300 font-medium" data-id="element-2280" data-mp="23hlpz3joihtd">
                {post.quickInfo.map((info, i) => <span key={i} data-id="element-2281" data-mp="1zhuu4lfapbhz" data-mpi={i}>{info}</span>)}
              </div>
            </section>}

          {/* Materials You'll Need */}
          {post.materialsNeeded && <section className="py-16 border-b border-stone-200 dark:border-white/5" data-id="element-2282" data-mp="1pp5mk1mv8hrx">
              <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-2283" data-mp="1krdrwzuu6hk1">
                <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} data-id="element-2284" data-mp="1kp14i76b7ezl">
                  <h2 className="font-heading text-3xl font-bold text-brand-charcoal dark:text-white mb-6" data-id="element-2285" data-mp="3ndsw5ftzt9yf">
                    Materials You'll Need
                  </h2>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-2286" data-mp="3rh7cy8pr9ucx">
                    {post.materialsNeeded.map(item => <div key={item.name} className="flex items-start gap-3 p-4 bg-white dark:bg-[#373737] rounded-xl border border-stone-200 dark:border-white/5" data-id="element-2287" data-mp="70cw6lic45p">
                        <CheckCircle2 className="w-5 h-5 text-brand-orange mt-0.5 shrink-0" data-id="element-2288" data-mp="33r4o01h514ij" />
                        <div data-id="element-2289" data-mp="38peh86jaxv1i">
                          {item.slug ? <Link to={`/shop/${item.slug}`} className="font-bold text-brand-orange hover:underline text-sm" data-id="element-2290" data-mp="3dnptsp0zejnf">
                              {item.name}
                            </Link> : <p className="font-bold text-brand-charcoal dark:text-white text-sm" data-id="element-2291" data-mp="3igiyxe0d3u01">
                              {item.name}
                            </p>}
                          <p className="text-xs text-brand-muted dark:text-stone-400 mt-0.5" data-id="element-2292" data-mp="2pwu5w2znc8lg">
                            {item.use}
                          </p>
                        </div>
                      </div>)}
                  </div>
                  <div className="mt-6 flex flex-col sm:flex-row gap-3" data-id="element-2293" data-mp="reici4bzxyrm">
                    <button onClick={() => setShowCalcModal(true)} className="inline-flex items-center justify-center gap-2 px-5 py-2.5 bg-brand-orange hover:bg-brand-orange-hover text-white font-bold text-sm rounded-none transition-colors" data-id="element-2294" data-mp="15916pshsdvyu">
                      <Calculator className="w-4 h-4" data-id="element-2295" data-mp="1a72zx68xfbo1" />
                      Calculate How Much You Need
                    </button>
                    <Link to="/#materials" className="inline-flex items-center justify-center gap-2 px-5 py-2.5 border border-stone-300 dark:border-[#555555] text-brand-charcoal dark:text-white font-bold text-sm rounded-none hover:bg-stone-100 dark:hover:bg-[#444444] transition-colors" data-id="element-2296" data-mp="1o5j3iobhsm1y">
                      Browse Our Catalog
                    </Link>
                  </div>
                </motion.div>
              </div>
            </section>}

          {/* Steps */}
          {post.steps && <section className="py-16" data-id="element-2297" data-mp="25g6b8m07reik">
              <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-2298" data-mp="25f51qlyuj02y">
                <div className="space-y-20" data-id="element-2299" data-mp="1rnqeu41tieqm">
                  {post.steps.map(step => <motion.article key={step.number} initial={{
              opacity: 0,
              y: 30
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true,
              margin: '-50px'
            }} className="relative" data-id="element-2300" data-mp="1vple7306ui1r">
                      <div className="flex items-center gap-4 mb-6" data-id="element-2301" data-mp="18qfgqmrx9rcr">
                        <span className="flex items-center justify-center w-10 h-10 bg-brand-orange text-white font-heading font-extrabold text-lg rounded-none shrink-0" data-id="element-2302" data-mp="1cr949f1gd454">
                          {step.number}
                        </span>
                        <h3 className="font-heading text-2xl md:text-3xl font-bold text-brand-charcoal dark:text-white" data-id="element-2303" data-mp="3748s1vfzl4nh">
                          {step.title}
                        </h3>
                      </div>

                      <div className="w-full h-64 md:h-80 rounded-xl overflow-hidden mb-6" data-id="element-2304" data-mp="ew6iazxj4cty">
                        <img src={step.image} alt={step.title} className="w-full h-full object-cover" data-id="element-2305" data-mp="awxpbxabcizg" />
                      </div>

                      <p className="text-brand-charcoal/85 dark:text-stone-300 text-base leading-relaxed mb-6" data-id="element-2306" data-mp="5z4h6ypg9ewj">
                        {step.description}
                      </p>

                      <div className="bg-stone-50 dark:bg-[#333333] border border-stone-200 dark:border-white/5 rounded-xl p-5" data-id="element-2307" data-mp="ymikdejqr6p3">
                        <p className="text-xs font-bold text-brand-muted dark:text-stone-400 uppercase tracking-wider mb-3" data-id="element-2308" data-mp="ovxed61qmzw5">
                          Pro Tips
                        </p>
                        <ul className="space-y-2" data-id="element-2309" data-mp="1md4hwbw4d8ku">
                          {step.tips.map((tip, i) => <li key={i} className="flex items-start gap-2 text-sm text-brand-charcoal/80 dark:text-stone-300" data-id="element-2310" data-mp="1hj9zrcw5x1nv" data-mpi={i}>
                              <CheckCircle2 className="w-4 h-4 text-brand-orange mt-0.5 shrink-0" data-id="element-2311" data-mp="1tusvkc6kriag" />
                              {tip}
                            </li>)}
                        </ul>
                      </div>

                      {step.material && <div className="mt-4 flex items-center gap-3 p-4 bg-brand-orange/10 border border-brand-orange/20 dark:border-white/5 rounded-xl" data-id="element-2312" data-mp="22wjyxadn7d15">
                          <span className="text-sm font-bold text-brand-orange" data-id="element-2313" data-mp="2ctoozlalzsn8">
                            Recommended:
                          </span>
                          <Link to={`/shop/${step.materialSlug}`} className="text-sm text-brand-charcoal dark:text-white font-medium hover:text-brand-orange transition-colors" data-id="element-2314" data-mp="2hlpw6ssx6t6e">
                            {step.material}
                          </Link>
                          <Link to={`/shop/${step.materialSlug}`} className="ml-auto text-xs font-bold text-brand-orange hover:underline" data-id="element-2315" data-mp="2vjwn1ozgbswk">
                            View Product →
                          </Link>
                        </div>}
                    </motion.article>)}
                </div>

                {/* SEO Blocks */}
                {post.seoBlocks && post.seoBlocks.map((block, idx) => <motion.div key={idx} initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} className={`mt-${idx === 0 ? '20' : '12'} p-6 md:p-8 ${idx % 2 === 0 ? 'bg-white dark:bg-[#333333]' : 'bg-stone-50 dark:bg-[#373737]'} border border-stone-200 dark:border-white/5 rounded-xl`} data-id="element-2316" data-mp="3lu7sq5wwhqev" data-mpi={idx}>
                      <h3 className="font-heading text-xl md:text-2xl font-bold text-brand-charcoal dark:text-white mb-3" data-id="element-2317" data-mp="3336l6thpsmjp">
                        {block.title}
                      </h3>
                      {block.content}
                    </motion.div>)}
              </div>
            </section>}
        </> /* Standard Article Layout */ : <section className="py-16" data-id="element-2318" data-mp="pw8bapspbex2">
          <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-2319" data-mp="pv6yogw1ekd8">
            {post.content}
          </div>
        </section>}

      {/* CTA */}
      <section className="py-16 bg-[#333333] border-t border-[#4a4a4a]" data-id="element-2320" data-mp="fzlt68rrf1yq">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-2321" data-mp="kxnl7j75ybmc">
          <h2 className="font-heading text-3xl md:text-4xl font-extrabold text-white mb-4" data-id="element-2322" data-mp="kvkm89o1985h">
            Ready to Get Started?
          </h2>
          <p className="text-stone-300 font-medium font-sub mb-8 max-w-lg mx-auto" data-id="element-2323" data-mp="1dmuiyfysmx6j">
            We deliver gravel, crushed rock, and all the materials you need
            straight to your driveway. Same-day delivery available.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center" data-id="element-2324" data-mp="13mlkswl8r1oi">
            <Link to="/#materials" className="px-8 py-3.5 bg-brand-orange hover:bg-brand-orange-hover text-white font-bold rounded-none transition-colors flex items-center justify-center" data-id="element-2325" data-mp="3kc95dlijgwby">
              Order Materials
              <ArrowRight className="w-5 h-5 ml-2" data-id="element-2326" data-mp="3d6war3ye892p" />
            </Link>
            <a href="tel:206-619-3742" className="px-8 py-3.5 border border-white/30 bg-white/10 text-white font-bold rounded-none hover:bg-white hover:text-stone-900 transition-all flex items-center justify-center" data-id="element-2327" data-mp="3i45ymf64wcof">
              <Phone className="w-4 h-4 mr-2" data-id="element-2328" data-mp="38duv2tqd45i2" />
              (206) 619-3742
            </a>
          </div>
        </div>
      </section>

      {/* Calculator Modal */}
      <AnimatePresence data-id="element-2329" data-mp="2zeznbd7o6ncn">
        {showCalcModal && <div className="fixed inset-0 z-50 flex items-center justify-center p-4 sm:p-6" data-id="element-2330" data-mp="2zg10poc5rlxj">
            <motion.div initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} exit={{
          opacity: 0
        }} onClick={() => setShowCalcModal(false)} className="absolute inset-0 bg-black/60 backdrop-blur-sm" data-id="element-2331" data-mp="2lgrjclc7ulrz" />
            <motion.div initial={{
          opacity: 0,
          scale: 0.95,
          y: 20
        }} animate={{
          opacity: 1,
          scale: 1,
          y: 0
        }} exit={{
          opacity: 0,
          scale: 0.95,
          y: 20
        }} className="relative w-full max-w-4xl bg-[#474747] border border-[#555555] rounded-2xl shadow-2xl overflow-hidden flex flex-col max-h-[85vh]" data-id="element-2332" data-mp="npoww8f88wro">
              <div className="flex items-center justify-between p-6 border-b border-[#555555] shrink-0" data-id="element-2333" data-mp="2d62bgff8xjya">
                <h3 className="text-xl font-bold text-white flex items-center" data-id="element-2334" data-mp="2ruf7fcec32ss">
                  <Calculator className="w-5 h-5 mr-2 text-brand-orange" data-id="element-2335" data-mp="2mwn7bv3jcadi" />
                  Material Calculator
                </h3>
                <button onClick={() => setShowCalcModal(false)} className="text-stone-400 hover:text-white transition-colors p-1" data-id="element-2336" data-mp="30ullp0huw6t0">
                  <X className="w-6 h-6" data-id="element-2337" data-mp="2vupdep24lt7n" />
                </button>
              </div>
              <div className="overflow-y-auto" data-id="element-2338" data-mp="16i41y2w87ot4">
                <MaterialCalculator variant="modal" data-id="element-2339" data-mp="11jamw61ajpqu" />
              </div>
            </motion.div>
          </div>}
      </AnimatePresence>
    </main>;
}